/**
 * Commitment Registry — the list of commitments.
 *
 * The items list's shell (EntityListPage), set up for commitments: tiles across
 * the top that count and filter (Active, Uncovered, Due in 30 days), search,
 * filters, sort, group and columns, and a peek under each row. A commitment's
 * own page is /prioritization/commitments/:commitmentId (CommitmentDetailPage),
 * drawn in the item page's frame. Build contract §1E: one list shell, configured
 * by entity. Until 2026-11-03 this page was a narrow card column with the
 * commitment beside it, which held a few and failed at many.
 *
 * Coverage is derived server-side (get_commitment_coverage RPC):
 *   covered  — all linked items are in an active cycle
 *   partial  — some linked items are in an active cycle
 *   at-risk  — no linked item is in any active cycle
 *   no-items — no items linked yet
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Handshake, Trash2 } from 'lucide-react';
import { EntityListPage, type EntityListTile } from '../../components/collection/EntityListPage';
import type { FacetDefinition, GroupOption, SortOption, TableColumnWithRender } from '../../components/collection/types';
import { Field, Modal, TextInput } from '../../components/ds';
import { ChoiceList } from '../../components/shared/ChoiceList';
import { EditorError } from '../../components/detail-page/detailPageParts';
import { LABEL_MONO, btnDanger, btnPrimary, btnSecondary, status } from '../../components/mono/primitives';
import { useCommitmentStore } from '../../stores/prioritization/commitmentStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { parseCommitmentPrefill, type CommitmentPrefill } from '../../lib/commitmentPrefill';
import { listAuthorityCoverage, type AuthorityCoverageRow } from '../../lib/assignmentService';
import { BindByHierarchySwitch } from '../../components/prioritization/commitments/BindByHierarchySwitch';
import { AuthorityCoverageLine } from '../../components/prioritization/commitments/AuthorityCoverageLine';
import {
  COMMITMENT_COVERAGE,
  COMMITMENT_PRIORITY_LABELS,
  COMMITMENT_PRIORITY_RANK,
  COMMITMENT_PRIORITY_TONE,
  COMMITMENT_STATUS_LABELS,
  COMMITMENT_STATUS_TONE,
  COMMITMENT_TYPE_LABELS,
  coverageCountLine,
  dueLine,
  dueTone,
  formatDueDate,
  isDueWithin30Days,
  isUncovered,
} from '../../components/prioritization/commitments/commitmentDisplay';
import type { CommitmentType, CommitmentWithCoverage, CreateCommitmentInput } from '../../types/prioritization/commitment';

/** A commitment as the list sorts and filters it. */
interface CommitmentListRow extends CommitmentWithCoverage {
  priorityRank: number;
  /** Binding assignments (commitment_authority_coverage_v); null when not read. */
  bindingCount: number | null;
  /** Sorts undated last. */
  dueSort: string;
}

const LIST_PATH = '/prioritization/commitments';

const FACETS: FacetDefinition<string>[] = [
  {
    key: 'status',
    label: 'Status',
    multi: true,
    options: (Object.keys(COMMITMENT_STATUS_LABELS) as (keyof typeof COMMITMENT_STATUS_LABELS)[]).map((s) => ({ value: s, label: COMMITMENT_STATUS_LABELS[s] })),
  },
  {
    key: 'commitmentType',
    label: 'Type',
    multi: true,
    options: (Object.keys(COMMITMENT_TYPE_LABELS) as CommitmentType[]).map((t) => ({ value: t, label: COMMITMENT_TYPE_LABELS[t] })),
  },
  {
    key: 'coverage',
    label: 'Coverage',
    multi: true,
    options: (Object.keys(COMMITMENT_COVERAGE) as (keyof typeof COMMITMENT_COVERAGE)[]).map((c) => ({ value: c, label: COMMITMENT_COVERAGE[c].label })),
  },
  {
    key: 'priority',
    label: 'Priority',
    multi: true,
    options: (Object.keys(COMMITMENT_PRIORITY_LABELS) as (keyof typeof COMMITMENT_PRIORITY_LABELS)[]).map((p) => ({ value: p, label: COMMITMENT_PRIORITY_LABELS[p] })),
  },
];

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'dueSort', label: 'Due date', defaultDirection: 'asc' },
  { field: 'priorityRank', label: 'Priority', defaultDirection: 'asc' },
  { field: 'title', label: 'Title' },
  { field: 'counterparty', label: 'Counterparty' },
  { field: 'updatedAt', label: 'Last updated' },
];

const GROUP_OPTIONS: GroupOption<string>[] = [
  { field: 'commitmentType', label: 'Type', getGroupLabel: (k) => COMMITMENT_TYPE_LABELS[k as CommitmentType] ?? k },
  { field: 'status', label: 'Status', getGroupLabel: (k) => COMMITMENT_STATUS_LABELS[k as keyof typeof COMMITMENT_STATUS_LABELS] ?? k },
  { field: 'coverage', label: 'Coverage', getGroupLabel: (k) => COMMITMENT_COVERAGE[k as keyof typeof COMMITMENT_COVERAGE]?.label ?? k },
  { field: 'priority', label: 'Priority', getGroupLabel: (k) => COMMITMENT_PRIORITY_LABELS[k as keyof typeof COMMITMENT_PRIORITY_LABELS] ?? k },
];

const TILES: EntityListTile<CommitmentListRow>[] = [
  { id: 'all', label: 'All commitments', tone: 'neutral' },
  { id: 'active', label: 'Active', match: (c) => c.status === 'active', tone: 'green' },
  { id: 'uncovered', label: 'Uncovered', match: isUncovered, tone: (n) => (n > 0 ? 'coral' : 'quiet'), hint: 'Active, with no linked item in an active cycle' },
  { id: 'due-soon', label: 'Due in 30 days', match: (c) => c.status === 'active' && isDueWithin30Days(c.dueDate), tone: (n) => (n > 0 ? 'mustard' : 'quiet') },
];

/** The list opens on Active, as it always has; "all" in the URL shows every commitment. */
const DEFAULT_TILE = 'active';

export default function CommitmentRegistryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const { syncState, fetchFromSupabase, createCommitment, deleteCommitment, linkItem, getCommitmentsWithCoverage, commitments } = useCommitmentStore();
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);

  useEffect(() => {
    void fetchFromSupabase();
    void fetchItems();
  }, [fetchFromSupabase, fetchItems]);

  // ── Authority coverage (983): binding assignments by who authorized them ────
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [authority, setAuthority] = useState<AuthorityCoverageRow[]>([]);
  const lastSyncAt = syncState.lastSyncAt;
  useEffect(() => {
    if (!workspaceId) return;
    let live = true;
    listAuthorityCoverage(workspaceId)
      .then((rows) => live && setAuthority(rows))
      // The readout is a reading, not a gate: on a failed read the line is not drawn.
      .catch(() => live && setAuthority([]));
    return () => {
      live = false;
    };
  }, [workspaceId, lastSyncAt]);

  // getCommitmentsWithCoverage reads the store; recompute when its commitments change.
  const rows: CommitmentListRow[] = useMemo(() => {
    const binding = new Map(authority.map((a) => [a.commitment_id, a.binding_count]));
    return getCommitmentsWithCoverage().map((c) => ({
      ...c,
      priorityRank: COMMITMENT_PRIORITY_RANK[c.priority],
      bindingCount: binding.get(c.id) ?? null,
      dueSort: c.dueDate ?? '9999-12-31',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitments, authority, getCommitmentsWithCoverage, syncState.lastSyncAt]);

  const itemTitle = useMemo(() => new Map(items.map((i) => [i.id, i.title ?? '(untitled)'])), [items]);

  // ── The tile pressed is kept in the URL, so Back to commitments returns to it ──
  const showParam = params.get('show');
  const activeTileId = showParam === 'all' ? null : showParam ?? DEFAULT_TILE;
  const setActiveTile = useCallback(
    (id: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === DEFAULT_TILE) next.delete('show');
          else next.set('show', id ?? 'all');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const listHref = `${LIST_PATH}${location.search}`;
  const openCommitment = useCallback(
    (id: string) => navigate(`${LIST_PATH}/${id}`, { state: { back: listHref } }),
    [navigate, listHref],
  );

  // ── Create, opened by New commitment or by a decision (RationalePanel prefill) ──
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState<CommitmentPrefill | null>(null);
  useEffect(() => {
    const p = parseCommitmentPrefill(location.state);
    if (!p) return;
    setPrefill(p);
    setCreating(true);
    // Consumed once: a refresh or Back must not open the form again.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = useCallback(
    async (input: CreateCommitmentInput): Promise<string | null> => {
      const created = await createCommitment(input);
      if (!created) return useCommitmentStore.getState().lastCreateError ?? 'The commitment was not created. Try again.';
      let note: string | null = null;
      if (prefill?.itemId && !(await linkItem(created.id, prefill.itemId))) {
        note = "Created, but it could not be linked to the decision's item. Link it from Linked items.";
      }
      setCreating(false);
      setPrefill(null);
      navigate(`${LIST_PATH}/${created.id}`, { state: { back: listHref, note } });
      return null;
    },
    [createCommitment, linkItem, navigate, prefill, listHref],
  );

  // ── Delete, from the row menu ───────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState<CommitmentListRow | null>(null);

  const tableColumns = useMemo((): TableColumnWithRender<CommitmentListRow>[] => [
    {
      key: 'title',
      label: 'Commitment',
      sortable: true,
      minWidth: 240,
      flex: true,
      render: (c) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-[var(--text-pri)]">{c.title}</span>
          {c.counterparty && <span className="block truncate text-xs text-[var(--text-ter)]">{c.counterparty}</span>}
        </div>
      ),
    },
    { key: 'commitmentType', label: 'Type', sortable: true, minWidth: 100, render: (c) => <span className="text-xs text-[var(--text-sec)]">{COMMITMENT_TYPE_LABELS[c.commitmentType]}</span> },
    { key: 'status', label: 'Status', sortable: true, minWidth: 100, render: (c) => <span {...status(COMMITMENT_STATUS_TONE[c.status])}>{COMMITMENT_STATUS_LABELS[c.status]}</span> },
    { key: 'priorityRank', label: 'Priority', sortable: true, minWidth: 96, render: (c) => <span {...status(COMMITMENT_PRIORITY_TONE[c.priority])}>{COMMITMENT_PRIORITY_LABELS[c.priority]}</span> },
    { key: 'dueSort', label: 'Due', sortable: true, minWidth: 110, render: (c) => <span {...status(dueTone(c.dueDate), 'normal-case tracking-normal font-sans text-xs')}>{formatDueDate(c.dueDate)}</span> },
    { key: 'coverage', label: 'Coverage', sortable: true, minWidth: 104, render: (c) => <span {...status(COMMITMENT_COVERAGE[c.coverage].tone)}>{COMMITMENT_COVERAGE[c.coverage].label}</span> },
    { key: 'linkedItemCount', label: 'Items', sortable: true, align: 'right', minWidth: 64, render: (c) => <span className="tabular-nums text-xs text-[var(--text-ter)]">{c.linkedItemCount}</span> },
    { key: 'bindingCount', label: 'Assignments', sortable: true, align: 'right', minWidth: 104, render: (c) => <span className="tabular-nums text-xs text-[var(--text-ter)]" title="Binding assignments">{c.bindingCount ?? '—'}</span> },
    { key: 'ownerName', label: 'Owner', sortable: true, minWidth: 110, render: (c) => <span className="truncate text-xs text-[var(--text-sec)]">{c.ownerName || '—'}</span> },
  ], []);

  // ?commitment=<id> was the old link to one commitment (an assignment ask, 981); it opens the page.
  const legacyId = params.get('commitment');
  if (legacyId) return <Navigate to={`${LIST_PATH}/${legacyId}?tab=assignments`} replace state={{ fromAsk: true }} />;

  return (
    <>
      <EntityListPage<CommitmentListRow, string>
        items={rows}
        loading={syncState.isLoading}
        getId={(c) => c.id}
        masterDetail={false}
        title="Commitment Registry"
        subtitle="Commitments to customers, partners and regulators, and whether active cycle priorities cover them."
        icon={<Handshake className="h-4 w-4 text-[var(--text-ter)]" />}
        itemLabel="commitment"
        searchFields={['title', 'counterparty', 'description', 'ownerName']}
        searchPlaceholder="Search commitments…"
        sortOptions={SORT_OPTIONS}
        defaultSort={{ field: 'dueSort', direction: 'asc' }}
        groupOptions={GROUP_OPTIONS}
        facets={FACETS}
        tableColumns={tableColumns}
        viewModes={['table', 'card']}
        defaultViewMode="table"
        renderCard={(c) => <CommitmentCard commitment={c} />}
        selectable={false}
        tiles={TILES}
        activeTileId={activeTileId}
        onActiveTileChange={setActiveTile}
        onItemClick={(c) => openCommitment(c.id)}
        renderPeek={(c) => <CommitmentPeek commitment={c} itemTitle={itemTitle} onOpen={() => openCommitment(c.id)} />}
        renderRowActions={(c) => (
          <button
            type="button"
            className="p-1 text-[var(--text-ter)] opacity-0 transition-opacity hover:text-[var(--coral)] group-hover:opacity-100 focus:opacity-100"
            aria-label={`Delete ${c.title}`}
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete(c);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
        onRefresh={() => void fetchFromSupabase()}
        onCreate={() => setCreating(true)}
        createLabel="New commitment"
        emptyTitle="No commitments yet"
        emptyDescription="Record a promise made to a customer, partner, regulator or team, then link the items that will keep it."
        emptyAction={{ label: 'New commitment', onClick: () => setCreating(true) }}
        headerExtra={<RegistryReadouts row={authority[0]} />}
        shellClassName="h-[calc(100vh-6rem)]"
      />

      {creating && (
        <CreateCommitmentModal
          prefill={prefill}
          onCreate={handleCreate}
          onClose={() => {
            setCreating(false);
            setPrefill(null);
          }}
        />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete this commitment?"
        description={confirmDelete ? `“${confirmDelete.title}” and its item links are removed. This cannot be undone.` : undefined}
        footer={
          <>
            <button {...btnSecondary()} onClick={() => setConfirmDelete(null)}>
              Cancel
            </button>
            <button
              {...btnDanger()}
              onClick={async () => {
                if (confirmDelete) await deleteCommitment(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Delete
            </button>
          </>
        }
      />
    </>
  );
}

// ── Under the header: the workspace's rule and the Registry's authority line ──

function RegistryReadouts({ row }: { row: AuthorityCoverageRow | undefined }) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  if (!workspaceId) return null;
  return (
    <div className="flex-shrink-0 space-y-1 border-b border-[var(--rule)] px-4 py-2 md:px-6">
      <BindByHierarchySwitch workspaceId={workspaceId} />
      <AuthorityCoverageLine row={row} scope="workspace" />
    </div>
  );
}

// ── A card, for a phone and the card view ───────────────────────────────────

function CommitmentCard({ commitment: c }: { commitment: CommitmentListRow }) {
  const coverage = COMMITMENT_COVERAGE[c.coverage];
  return (
    <div className="space-y-1.5 p-4">
      <div className="font-medium text-[var(--text-pri)]">{c.title}</div>
      {c.counterparty && <div className="truncate text-xs text-[var(--text-ter)]">{c.counterparty}</div>}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span {...status(COMMITMENT_STATUS_TONE[c.status])}>{COMMITMENT_STATUS_LABELS[c.status]}</span>
        <span {...status(COMMITMENT_PRIORITY_TONE[c.priority])}>{COMMITMENT_PRIORITY_LABELS[c.priority]}</span>
        <span {...status(coverage.tone)}>{coverage.label}</span>
      </div>
      <div className="text-xs text-[var(--text-dim)]">
        {COMMITMENT_TYPE_LABELS[c.commitmentType]} · {dueLine(c.dueDate)}
      </div>
    </div>
  );
}

// ── The peek: what you would want to know before opening it ─────────────────

function CommitmentPeek({
  commitment: c,
  itemTitle,
  onOpen,
}: {
  commitment: CommitmentListRow;
  itemTitle: Map<string, string>;
  onOpen: () => void;
}) {
  const coverage = COMMITMENT_COVERAGE[c.coverage];
  const covered = new Set(c.coveredItemIds);
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <div className={LABEL_MONO}>Coverage</div>
          <div {...status(coverage.tone)}>{coverage.label}</div>
          <div className="text-[13px] text-[var(--text-dim)]">{coverageCountLine(c) ?? coverage.detail}</div>
        </div>
        <div>
          <div className={LABEL_MONO}>Due</div>
          <div className="font-semibold text-[var(--text-pri)]">{formatDueDate(c.dueDate)}</div>
          <div className="text-[13px] text-[var(--text-dim)]">{dueLine(c.dueDate)}</div>
        </div>
        <div>
          <div className={LABEL_MONO}>Assignments</div>
          <div className="font-semibold text-[var(--text-pri)]">{c.bindingCount ?? '—'}</div>
          <div className="text-[13px] text-[var(--text-dim)]">binding</div>
        </div>
      </div>
      {c.description && <p className="max-w-[84ch] whitespace-pre-wrap text-[var(--text-sec)]">{c.description}</p>}
      {c.linkedItemIds.length > 0 && (
        <div>
          <div className={LABEL_MONO}>Linked items</div>
          <ul className="mt-1 space-y-0.5">
            {c.linkedItemIds.slice(0, 5).map((id) => (
              <li key={id} className="flex items-baseline gap-2">
                <Link to={`/prioritization/items/${id}`} className="truncate text-[var(--text-pri)] underline decoration-[var(--rule-bright)] underline-offset-[3px]" onClick={(e) => e.stopPropagation()}>
                  {itemTitle.get(id) ?? 'An item'}
                </Link>
                <span {...status(covered.has(id) ? 'green' : 'mustard', 'normal-case tracking-normal font-sans text-xs')}>
                  {covered.has(id) ? 'in an active cycle' : 'in no active cycle'}
                </span>
              </li>
            ))}
            {c.linkedItemIds.length > 5 && <li className="text-xs text-[var(--text-dim)]">and {c.linkedItemIds.length - 5} more</li>}
          </ul>
        </div>
      )}
      <button {...btnSecondary()} onClick={onOpen}>
        Open this commitment&apos;s page
      </button>
    </div>
  );
}

// ── New commitment ───────────────────────────────────────────────────────────

function CreateCommitmentModal({
  prefill,
  onCreate,
  onClose,
}: {
  prefill: CommitmentPrefill | null;
  onCreate: (input: CreateCommitmentInput) => Promise<string | null>;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(prefill?.title ?? '');
  const [type, setType] = useState<CommitmentType>(prefill ? 'internal' : 'customer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!title.trim()) {
      setError('A commitment needs a title.');
      return;
    }
    setSaving(true);
    setError(null);
    const err = await onCreate({ title: title.trim(), commitmentType: type, ...(prefill ? { description: prefill.description } : {}) });
    setSaving(false);
    if (err) setError(err);
  };
  return (
    <Modal
      open
      onClose={onClose}
      label="Commitment Registry"
      title="New commitment"
      description={prefill ? `From the decision “${prefill.decisionTitle}”.` : 'A promise the organization has made. Its page opens once it is created.'}
      footer={
        <>
          <button {...btnSecondary()} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button {...btnPrimary()} onClick={() => void submit()} disabled={saving}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" htmlFor="new-commitment-title">
          <TextInput
            id="new-commitment-title"
            value={title}
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
        </Field>
        <div>
          <div className={`${LABEL_MONO} mb-1.5`}>Made to</div>
          <ChoiceList
            label="Type"
            compact
            value={type}
            onPick={(v) => setType(v as CommitmentType)}
            options={(Object.keys(COMMITMENT_TYPE_LABELS) as CommitmentType[]).map((t) => ({ value: t, label: COMMITMENT_TYPE_LABELS[t] }))}
          />
        </div>
        <EditorError error={error} />
      </div>
    </Modal>
  );
}
